<template>
  <div class="wf-field-config">
    <div v-if="!field" class="wf-empty">请在中间画布选择一个字段</div>
    <el-form v-else label-width="96px" size="small">
      <el-form-item label="标题">
        <el-input v-model="field.label" placeholder="字段标题" maxlength="30" show-word-limit />
      </el-form-item>

      <el-form-item label="字段key" v-if="field.type !== 'tips'">
        <el-input v-model="field.key" placeholder="英文唯一标识" />
        <div class="wf-hint">用于表单数据键名，需唯一</div>
      </el-form-item>

      <!-- 占位 -->
      <el-form-item label="占位提示" v-if="['input','textarea','number','date'].includes(field.type)">
        <el-input v-model="field.placeholder" placeholder="占位提示" />
      </el-form-item>

      <!-- 金额单位 -->
      <el-form-item label="单位" v-if="field.type === 'money'">
        <el-input v-model="field.unit" placeholder="如：元" style="width: 160px" />
      </el-form-item>

      <!-- 金额：千分位 + 人民币大写 -->
      <el-form-item label="显示大写" v-if="field.type === 'money'">
        <el-switch :value="field.showUpper !== false" @change="$set(field, 'showUpper', $event)" />
        <div class="wf-hint">开启后金额按千分位显示，并在输入框下方展示人民币大写</div>
      </el-form-item>

      <!-- 商机/项目：数据范围（默认范围 + 条件范围） -->
      <template v-if="isScopeField">
        <el-form-item label="默认范围">
          <el-radio-group v-model="field.scope" size="mini">
            <el-radio-button label="all">{{ scopeAllLabel }}</el-radio-button>
            <el-radio-button label="mine">{{ scopeMineLabel }}</el-radio-button>
          </el-radio-group>
          <div class="wf-hint">未命中下方条件时生效；{{ scopeMineHint }}</div>
        </el-form-item>
        <el-form-item label="条件范围">
          <div v-for="(r, i) in field.scopeRules || []" :key="i" class="wf-ct-card">
            <div class="wf-col-line">
              <el-select v-model="r.key" size="mini" placeholder="选择字段" style="flex: 1" @change="onScopeRuleKeyChange(r)">
                <el-option v-for="d in scopeRuleFieldOptions" :key="d.key" :label="d.label || d.key" :value="d.key" />
              </el-select>
              <el-select v-model="r.scope" size="mini" style="width: 120px">
                <el-option :label="scopeAllLabel" value="all" />
                <el-option :label="scopeMineLabel" value="mine" />
              </el-select>
              <i class="el-icon-delete wf-opt-del" @click="removeScopeRule(i)"></i>
            </div>
            <div class="wf-col-line">
              <el-select
                v-model="r.values"
                size="mini"
                multiple
                filterable
                allow-create
                default-first-option
                placeholder="命中取值（留空=该字段有值即命中）"
                style="flex: 1"
              >
                <el-option v-for="o in scopeRuleValueOptions(r)" :key="o.value" :label="o.label" :value="o.value" />
              </el-select>
            </div>
          </div>
          <el-button size="mini" icon="el-icon-plus" @click="addScopeRule">添加条件</el-button>
          <div class="wf-hint">当所选字段取值命中时，按此范围取数；自上而下首个命中生效</div>
        </el-form-item>
      </template>

      <!-- 多选 -->
      <el-form-item label="是否多选" v-if="['user','dept','select'].includes(field.type)">
        <el-switch v-model="field.multiple" />
      </el-form-item>

      <!-- 部门：中心范围（限定只显示所选中心下的部门） -->
      <el-form-item label="中心范围" v-if="field.type === 'dept'">
        <el-select
          v-model="centerScope"
          size="mini"
          multiple
          collapse-tags
          clearable
          filterable
          placeholder="不限制（全部中心）"
          style="width: 100%"
        >
          <el-option v-for="c in centerOptions" :key="c.id" :label="c.name" :value="c.id" />
        </el-select>
        <div class="wf-hint">只列出所选中心下的部门；配置后中心本身不可勾选，只能选具体部门</div>
        <div class="wf-hint" v-if="!centerOptions.length">暂无中心数据，请稍后重试</div>
      </el-form-item>

      <!-- 成员：部门范围（关联「部门」字段，按其所选部门缩小成员选择范围） -->
      <el-form-item label="部门范围" v-if="field.type === 'user'">
        <el-select
          v-model="deptFilterKey"
          size="mini"
          clearable
          filterable
          placeholder="不限制（全部成员）"
          style="width: 100%"
        >
          <el-option v-for="d in deptFieldOptions" :key="d.key" :label="d.label || d.key" :value="d.key" />
        </el-select>
        <div class="wf-col-line wf-cond-line" v-if="deptFilterKey">
          <el-switch v-model="deptIncludeChildren" />
          <span class="wf-inline-hint">包含子部门成员</span>
        </div>
        <div class="wf-hint">按所选「部门」字段的当前取值过滤成员；部门未选择时显示全部成员；已选成员超出范围时自动清空</div>
        <div class="wf-hint" v-if="!deptFieldOptions.length">暂无可选部门字段，请先在表单中添加「部门」控件</div>
      </el-form-item>

      <!-- 只读文本：设计时写入的显示内容（支持 ${字段key} 引用） -->
      <el-form-item label="显示内容" v-if="field.type === 'readonlyText'">
        <el-input v-model="field.content" type="textarea" :rows="3" placeholder="填写后只读展示，如：客户 ${customer_name}" />
        <div v-if="readonlyPlaceholders.length" class="wf-hint">已引用：{{ readonlyPlaceholders.join('、') }}</div>
        <div v-if="unknownReadonlyKeys.length" class="wf-hint wf-hint-warn">引用无效：{{ unknownReadonlyKeys.join('、') }}</div>
        <div class="wf-hint">支持 ${字段key} 引用其他字段值，发起/审批时只读展示且随表单提交；留空则显示「—」</div>
        <div class="wf-hint">可引用 key（不可引用只读文本自身类字段）：{{ readonlyRefKeys.join('、') || '—' }}</div>
      </el-form-item>

      <!-- 必填（总价为自动计算字段、只读文本为固定内容字段，均无需必填） -->
      <el-form-item label="是否必填" v-if="field.type !== 'tips' && field.type !== 'total' && field.type !== 'sqlValue' && field.type !== 'readonlyText'">
        <el-switch v-model="field.required" />
      </el-form-item>

      <!-- 条件必填：依赖字段取值命中时为必填（与「是否必填」任一命中即必填） -->
      <el-form-item label="条件必填" v-if="canConditionalRequired">
        <el-switch v-model="condEnabled" />
        <template v-if="condEnabled">
          <div class="wf-col-line wf-cond-line">
            <el-select v-model="condKey" size="mini" placeholder="选择依赖字段" style="flex: 1" @change="onCondKeyChange">
              <el-option v-for="d in condFieldOptions" :key="d.key" :label="d.label || d.key" :value="d.key" />
            </el-select>
          </div>
          <div class="wf-col-line">
            <el-select v-model="condValues" size="mini" multiple placeholder="命中取值（可多选）" style="flex: 1">
              <el-option
                v-for="o in condValueOptions"
                :key="o.value"
                :label="(o.label || o.value) + '（' + o.value + '）'"
                :value="o.value"
              />
            </el-select>
          </div>
          <div class="wf-hint">依赖字段取值命中以上选项时本字段必填；命中取值留空表示依赖字段有值即必填</div>
          <div class="wf-hint" v-if="!condFieldOptions.length">暂无可选依赖字段，请先添加带选项的字段（下拉/单选/多选）</div>
        </template>
      </el-form-item>

      <!-- 附件：允许的附件类型（预设分类 + 自定义扩展名） -->
      <el-form-item label="附件类型" v-if="field.type === 'upload'">
        <el-checkbox-group :value="field.attachTypes || []" size="mini" class="wf-attach-types" @input="setAttachTypes">
          <el-checkbox v-for="t in attachTypeGroups" :key="t.value" :label="t.value">{{ t.label }}</el-checkbox>
        </el-checkbox-group>
        <el-input
          :value="field.attachExts || ''"
          size="mini"
          placeholder="自定义扩展名，如：dwg, rar（逗号分隔，可不填）"
          style="width: 100%; margin-top: 6px"
          @input="setAttachExts"
        />
        <div class="wf-hint">全部不选且无自定义扩展名时，不限制文件类型</div>
      </el-form-item>

      <!-- 明细列配置 -->
      <el-form-item label="明细列" v-if="field.type === 'detail'">
        <div v-for="(c, i) in (field.columns || [])" :key="c.key" class="wf-col-card">
          <div class="wf-col-line">
            <el-select v-model="c.type" size="mini" style="width: 120px" @change="onColTypeChange(c)">
              <el-option v-for="t in colTypes" :key="t.type" :label="t.label" :value="t.type" />
            </el-select>
            <el-input v-if="c.type === 'money'" v-model="c.unit" size="mini" placeholder="单位" style="width: 80px" />
            <el-checkbox v-if="c.type === 'money'" :value="c.showUpper !== false" @change="$set(c, 'showUpper', $event)" class="wf-col-req">大写</el-checkbox>
            <el-checkbox v-if="c.type !== 'sqlValue'" v-model="c.required" class="wf-col-req">必填</el-checkbox>
            <i class="el-icon-delete wf-opt-del" @click="removeColumn(i)"></i>
          </div>
          <div class="wf-col-line">
            <el-input v-model="c.label" size="mini" placeholder="列标题" maxlength="20" style="flex: 1" />
            <el-input v-model="c.key" size="mini" placeholder="列key" style="width: 140px" />
          </div>
          <div class="wf-col-line">
            <span class="wf-lv-idx">列宽</span>
            <el-input-number
              :value="c.width"
              size="mini"
              :min="60"
              :max="600"
              :step="10"
              controls-position="right"
              style="width: 110px"
              @input="v => setColWidth(c, v)"
            />
            <span class="wf-hint">px，留空自动均分</span>
            <el-button v-if="c.width" type="text" size="mini" @click="setColWidth(c)">恢复均分</el-button>
          </div>
          <!-- 级联子列：SQL 数据集与层级映射 -->
          <template v-if="c.type === 'cascade'">
            <el-input
              v-model="c.sql"
              type="textarea"
              :rows="3"
              class="wf-col-sql"
              placeholder="自定义SQL，列按层级命名：level1_value/level1_label、level2_value/level2_label…"
            />
            <div class="wf-col-line">
              <el-button size="mini" icon="el-icon-refresh" :loading="parsingColKey === c.key" @click="parseColSql(c)">解析</el-button>
              <span v-if="colParse[c.key]" class="wf-hint">共 {{ colParse[c.key].rows.length }} 行</span>
            </div>
            <div v-for="(lv, li) in c.levels || []" :key="li" class="wf-col-line">
              <span class="wf-lv-idx">L{{ li + 1 }}</span>
              <el-select v-model="lv.value" size="mini" placeholder="值列" style="flex: 1" filterable allow-create>
                <el-option v-for="pc in colColumns(c)" :key="pc" :label="pc" :value="pc" />
              </el-select>
              <el-select v-model="lv.label" size="mini" placeholder="显示列" style="flex: 1" filterable allow-create>
                <el-option v-for="pc in colColumns(c)" :key="pc" :label="pc" :value="pc" />
              </el-select>
              <i class="el-icon-delete wf-opt-del" @click="removeColLevel(c, li)"></i>
            </div>
            <el-button v-if="(c.levels || []).length" size="mini" icon="el-icon-plus" @click="addColLevel(c)">添加级</el-button>
          </template>
          <!-- 取值子列：SQL、测试参数、解析与取值列 -->
          <template v-if="c.type === 'sqlValue'">
            <el-input
              v-model="c.sql"
              type="textarea"
              :rows="3"
              class="wf-col-sql"
              placeholder="自定义SQL，取首行值；${key} 优先取本行同名列，回退表单字段，如：&#10;SELECT price FROM goods WHERE code = '${goods_code}'"
            />
            <div v-for="k in colSqlPlaceholders(c)" :key="c.key + '_' + k" class="wf-col-line">
              <span class="wf-lv-idx">${{ k }}</span>
              <el-input
                :value="(colParams[c.key] || {})[k]"
                size="mini"
                placeholder="测试值"
                style="flex: 1"
                @input="v => setColParam(c, k, v)"
              />
            </div>
            <div class="wf-col-line">
              <el-button size="mini" icon="el-icon-refresh" :loading="parsingColKey === c.key" @click="parseColSqlValue(c)">解析</el-button>
              <span v-if="colValueParse[c.key]" class="wf-hint">首行取值：{{ colValuePreview(c) }}</span>
            </div>
            <div class="wf-col-line" v-if="colValueColumns(c).length">
              <el-select v-model="c.valueCol" size="mini" placeholder="取值列(空=首列)" style="flex: 1" filterable allow-create clearable>
                <el-option v-for="pc in colValueColumns(c)" :key="pc" :label="pc" :value="pc" />
              </el-select>
            </div>
            <div class="wf-hint">每行按本行参数自动取数，只读展示；${} 可引用：{{ refKeysHint(c) }}</div>
          </template>
          <!-- 受控数字子列：约束条件 -->
          <template v-if="c.type === 'ctrlNumber'">
            <div v-for="(ct, i) in c.constraints || []" :key="i" class="wf-ct-card">
              <div class="wf-col-line">
                <el-select v-model="ct.op" size="mini" style="width: 100px">
                  <el-option v-for="o in ctrlOps" :key="o.value" :label="o.label" :value="o.value" />
                </el-select>
                <el-select v-model="ct.mode" size="mini" style="width: 100px">
                  <el-option label="固定值" value="fixed" />
                  <el-option label="引用值" value="ref" />
                </el-select>
                <i class="el-icon-delete wf-opt-del" @click="removeConstraint(c, i)"></i>
              </div>
              <div class="wf-col-line">
                <el-input-number v-if="ct.mode === 'fixed'" v-model="ct.value" size="mini" controls-position="right" style="width: 100%" />
                <el-input
                  v-else
                  :value="ct.refKey"
                  size="mini"
                  placeholder="引用key(本行子列/级联别名)"
                  @input="v => setConstraintRefKey(ct, v)"
                />
              </div>
            </div>
            <el-button size="mini" icon="el-icon-plus" @click="addConstraint(c)">添加约束</el-button>
            <div class="wf-hint">引用值支持本行子列key/级联层级别名：{{ refKeysHint(c) }}</div>
          </template>
          <!-- 商机子列：数据范围 -->
          <template v-if="c.type === 'chance'">
            <div class="wf-col-line">
              <span class="wf-lv-idx">范围</span>
              <el-radio-group v-model="c.scope" size="mini">
                <el-radio-button label="all">全部商机</el-radio-button>
                <el-radio-button label="mine">本人商机</el-radio-button>
              </el-radio-group>
            </div>
            <div class="wf-hint">本人商机 = 当前登录用户创建的商机</div>
          </template>
          <!-- 项目子列：数据范围 -->
          <template v-if="c.type === 'project'">
            <div class="wf-col-line">
              <span class="wf-lv-idx">范围</span>
              <el-radio-group v-model="c.scope" size="mini">
                <el-radio-button label="all">全部项目</el-radio-button>
                <el-radio-button label="mine">本人项目</el-radio-button>
              </el-radio-group>
            </div>
            <div class="wf-hint">本人项目 = 当前登录用户申请立项的项目</div>
          </template>
        </div>
        <el-button size="mini" icon="el-icon-plus" @click="addColumn">添加列</el-button>
        <div class="wf-hint">明细可添加多行，每行按上述子列填写</div>
      </el-form-item>

      <!-- 选项编辑 -->
      <el-form-item label="选项" v-if="['select','radio','checkbox'].includes(field.type)">
        <div v-for="(o, i) in field.options" :key="i" class="wf-opt-row">
          <el-input v-model="o.label" size="mini" placeholder="名称" style="width: 130px" />
          <el-input v-model="o.value" size="mini" placeholder="值" style="width: 100px" />
          <i class="el-icon-delete wf-opt-del" @click="removeOption(i)"></i>
        </div>
        <el-button size="mini" icon="el-icon-plus" @click="addOption">添加选项</el-button>
      </el-form-item>

      <!-- 级联：自定义SQL数据集 -->
      <template v-if="field.type === 'cascade'">
        <el-form-item label="数据源SQL">
          <el-input
            v-model="field.sql"
            type="textarea"
            :rows="6"
            placeholder="自定义SQL(单数据集)，列按层级命名，如：&#10;SELECT prov_code AS level1_value, prov_name AS level1_label,&#10;       city_code AS level2_value, city_name AS level2_label&#10;FROM area ORDER BY prov_code, city_code"
          />
          <div class="wf-hint">支持任意级数：结果列按 level1_value/level1_label、level2_value/level2_label… 命名；填写后点「解析并预览」</div>
        </el-form-item>
        <el-form-item label="解析预览">
          <el-button size="mini" icon="el-icon-refresh" :loading="parsing" @click="parseSql">解析并预览</el-button>
          <span v-if="parsedRows" class="wf-hint">共 {{ parsedRows.length }} 行（最多取5000行）</span>
        </el-form-item>
        <el-form-item label="层级映射" v-if="(field.levels || []).length">
          <div v-for="(lv, i) in field.levels" :key="i" class="wf-opt-row">
            <span class="wf-lv-idx">L{{ i + 1 }}</span>
            <el-select v-model="lv.value" size="mini" placeholder="值列" style="width: 130px" filterable allow-create>
              <el-option v-for="c in parsedColumns" :key="c" :label="c" :value="c" />
            </el-select>
            <el-select v-model="lv.label" size="mini" placeholder="显示列" style="width: 130px" filterable allow-create>
              <el-option v-for="c in parsedColumns" :key="c" :label="c" :value="c" />
            </el-select>
            <i class="el-icon-delete wf-opt-del" @click="removeLevel(i)"></i>
          </div>
          <el-button size="mini" icon="el-icon-plus" @click="addLevel">添加级</el-button>
          <div class="wf-hint">值列用于存储，显示列用于展示</div>
        </el-form-item>
        <el-form-item label="效果预览" v-if="previewTree.length">
          <el-cascader
            v-model="previewValue"
            :options="previewTree"
            :props="{ expandTrigger: 'hover' }"
            clearable
            filterable
            style="width: 100%"
          />
        </el-form-item>
      </template>

      <!-- 受控数字：约束条件 -->
      <template v-if="field.type === 'ctrlNumber'">
        <el-form-item label="约束条件">
          <div v-for="(ct, i) in field.constraints || []" :key="i" class="wf-col-card">
            <div class="wf-col-line">
              <el-select v-model="ct.op" size="mini" style="width: 110px">
                <el-option v-for="o in ctrlOps" :key="o.value" :label="o.label" :value="o.value" />
              </el-select>
              <el-select v-model="ct.mode" size="mini" style="width: 110px">
                <el-option label="固定值" value="fixed" />
                <el-option label="引用值" value="ref" />
              </el-select>
              <i class="el-icon-delete wf-opt-del" @click="removeConstraint(field, i)"></i>
            </div>
            <div class="wf-col-line">
              <el-input-number v-if="ct.mode === 'fixed'" v-model="ct.value" size="mini" controls-position="right" style="width: 100%" />
              <el-input
                v-else
                :value="ct.refKey"
                size="mini"
                placeholder="引用key(字段key/级联别名)"
                @input="v => setConstraintRefKey(ct, v)"
              />
            </div>
          </div>
          <el-button size="mini" icon="el-icon-plus" @click="addConstraint(field)">添加约束</el-button>
          <div class="wf-hint">填写时实时校验；引用值支持表单字段key/级联层级别名</div>
          <div class="wf-hint">可引用 key：{{ fieldRefKeysHint() }}</div>
        </el-form-item>
      </template>

      <!-- 总价：关联明细自动计算 -->
      <template v-if="field.type === 'total'">
        <el-form-item label="关联明细">
          <el-select v-model="field.detailKey" size="mini" placeholder="选择明细/表格字段" style="width: 100%" @change="onTotalDetailChange">
            <el-option v-for="d in detailFields" :key="d.key" :label="d.label" :value="d.key" />
          </el-select>
        </el-form-item>
        <el-form-item label="数量列" v-if="totalColumns.length">
          <el-select v-model="field.qtyCol" size="mini" placeholder="选择数量列" style="width: 100%" filterable>
            <el-option v-for="c in totalColumns" :key="c.key" :label="c.label" :value="c.key" />
          </el-select>
        </el-form-item>
        <el-form-item label="单价列" v-if="totalColumns.length">
          <el-select v-model="field.priceCol" size="mini" placeholder="选择单价列" style="width: 100%" filterable>
            <el-option v-for="c in totalColumns" :key="c.key" :label="c.label" :value="c.key" />
          </el-select>
        </el-form-item>
        <el-form-item label="单位">
          <el-input v-model="field.unit" size="mini" placeholder="如：元" style="width: 160px" />
        </el-form-item>
        <el-form-item>
          <div class="wf-hint">自动计算关联明细各行 数量×单价 之和，只读展示并随表单提交</div>
        </el-form-item>
      </template>

      <!-- 取值：自定义SQL首行值 -->
      <template v-if="field.type === 'sqlValue'">
        <el-form-item label="取值SQL">
          <el-input
            v-model="field.sql"
            type="textarea"
            :rows="5"
            placeholder="自定义SQL，取首行值；可用 ${字段key} 引用表单其他字段，如：&#10;SELECT price FROM goods WHERE code = '${goods_code}'"
          />
          <div class="wf-hint">取结果首行作为值；${字段key} 会随表单对应字段当前值实时替换并自动重取</div>
          <div class="wf-hint">可引用 key（表单字段）：{{ fieldRefKeysHint() }}</div>
        </el-form-item>
        <el-form-item label="测试参数" v-if="sqlPlaceholders.length">
          <div v-for="k in sqlPlaceholders" :key="k" class="wf-opt-row">
            <span class="wf-lv-idx">${{ k }}</span>
            <el-input v-model="paramTest[k]" size="mini" placeholder="测试值" />
          </div>
        </el-form-item>
        <el-form-item label="解析预览">
          <el-button size="mini" icon="el-icon-refresh" :loading="parsing" @click="parseSqlValue">解析并预览</el-button>
          <span v-if="sqlValueRow" class="wf-hint">首行取值：{{ sqlValuePreview }}</span>
        </el-form-item>
        <el-form-item label="取值列" v-if="sqlValueColumns.length">
          <el-select v-model="field.valueCol" size="mini" placeholder="默认首列" style="width: 100%" filterable allow-create clearable>
            <el-option v-for="c in sqlValueColumns" :key="c" :label="c" :value="c" />
          </el-select>
        </el-form-item>
      </template>

      <!-- 当前用户id 说明 -->
      <el-form-item label="说明" v-if="field.type === 'curUser'">
        <div class="wf-hint">发起/审批时自动填入当前登录用户的 userId，无需手动填写；只读态按成员姓名回显</div>
      </el-form-item>

      <!-- 用户角色：自动填入 + 是否显示 -->
      <template v-if="field.type === 'userRole'">
        <el-form-item label="说明">
          <div class="wf-hint">发起/审批时自动填入当前登录用户的 roleId，无需手动填写；只读态按角色名称回显</div>
        </el-form-item>
        <el-form-item label="是否显示">
          <el-switch v-model="field.visible" />
          <div class="wf-hint">关闭后该字段不在表单中展示，roleId 仍自动填充并随表单提交</div>
        </el-form-item>
      </template>

      <!-- 说明内容 -->
      <el-form-item label="说明内容" v-if="field.type === 'tips'">
        <el-input v-model="field.content" type="textarea" :rows="3" placeholder="说明文字" />
      </el-form-item>

      <el-form-item>
        <el-button size="mini" type="danger" plain icon="el-icon-delete" @click="$emit('remove')">删除该字段</el-button>
      </el-form-item>
    </el-form>
  </div>
</template>

<script>
import { DETAIL_COL_TYPES, createColumn, CTRL_NUMBER_OPS, ATTACH_TYPE_GROUPS, readonlyTextPlaceholders } from './fieldTypes'
import { queryDataset, querySqlValue } from '@/api/wflow/form'
import { parseLevelsFromColumns, buildCascadeTree } from '../../cascadeUtil'

export default {
  name: 'FieldConfig',
  props: {
    field: { type: Object, default: null },
    // 表单全部字段（供总价等聚合字段配置关联明细）
    fields: { type: Array, default: () => [] },
    // 角色列表（商机/项目条件数据范围中，驱动字段为「用户角色」时的取值选项）
    roleOptions: { type: Array, default: () => [] },
    // 一级组织（中心）列表（部门字段「中心范围」的取值选项）
    centerOptions: { type: Array, default: () => [] }
  },
  data() {
    return {
      colTypes: DETAIL_COL_TYPES,
      ctrlOps: CTRL_NUMBER_OPS,
      attachTypeGroups: ATTACH_TYPE_GROUPS,
      parsing: false,
      parsedRows: null,
      parsedColumns: [],
      previewValue: [],
      // 明细级联子列：解析状态与按列缓存 { colKey: { rows, columns } }
      parsingColKey: '',
      colParse: {},
      // 明细取值子列：首行缓存与测试参数 { colKey: row / params }
      colValueParse: {},
      colParams: {},
      // 取值字段：测试参数与首行预览
      paramTest: {},
      sqlValueRow: null
    }
  },
  computed: {
    previewTree() {
      if (!this.field || this.field.type !== 'cascade') return []
      return buildCascadeTree(this.parsedRows || [], this.field.levels || [])
    },
    // 可选的明细/表格字段
    detailFields() {
      return (this.fields || []).filter(x => x.type === 'detail')
    },
    // 已关联明细的子列
    totalColumns() {
      const d = this.detailFields.find(x => x.key === (this.field && this.field.detailKey))
      return d ? (d.columns || []) : []
    },
    // 商机/项目：是否需要数据范围配置
    isScopeField() {
      const t = this.field && this.field.type
      return t === 'chance' || t === 'project'
    },
    scopeAllLabel() {
      return this.field && this.field.type === 'chance' ? '全部商机' : '全部项目'
    },
    scopeMineLabel() {
      return this.field && this.field.type === 'chance' ? '本人商机' : '本人项目'
    },
    scopeMineHint() {
      return this.field && this.field.type === 'chance'
        ? '本人商机 = 当前登录用户创建（create_man）的商机'
        : '本人项目 = 当前登录用户申请立项（create_man_id）的项目'
    },
    // 条件数据范围：可作为驱动条件的字段（排除说明/明细/附件/取值/总价及自身）
    scopeRuleFieldOptions() {
      const skip = ['tips', 'detail', 'upload', 'sqlValue', 'total']
      return (this.fields || []).filter(x =>
        x.key !== (this.field && this.field.key) && skip.indexOf(x.type) < 0
      )
    },
    // 条件必填：可作为依赖的字段（含固定选项的下拉/单选/多选字段）
    condFieldOptions() {
      return (this.fields || []).filter(x =>
        x.key !== (this.field && this.field.key) &&
        Array.isArray(x.options) && x.options.length
      )
    },
    // 成员：可作为「部门范围」依赖的部门字段（排除自身）
    deptFieldOptions() {
      return (this.fields || []).filter(x =>
        x.type === 'dept' && x.key !== (this.field && this.field.key)
      )
    },
    // 部门：限定的中心范围（一级组织 id 数组；[] 或缺失 = 不限制，显示全部中心）
    centerScope: {
      get() {
        return (this.field && Array.isArray(this.field.centerScope)) ? this.field.centerScope : []
      },
      set(v) {
        if (!this.field) return
        const list = Array.isArray(v) ? v : []
        if (!list.length) {
          this.$delete(this.field, 'centerScope')
          return
        }
        this.$set(this.field, 'centerScope', list)
      }
    },
    // 成员：关联的部门字段 key（'' = 不限制，显示全部成员）
    deptFilterKey: {
      get() {
        return (this.field && this.field.deptFilterKey) || ''
      },
      set(v) {
        if (!this.field) return
        if (!v) {
          this.$delete(this.field, 'deptFilterKey')
          return
        }
        this.$set(this.field, 'deptFilterKey', v)
      }
    },
    // 成员：是否包含所选部门的子部门成员（缺省即开启）
    deptIncludeChildren: {
      get() {
        return !this.field || this.field.deptIncludeChildren !== false
      },
      set(v) {
        if (this.field) this.$set(this.field, 'deptIncludeChildren', !!v)
      }
    },
    // 条件必填：依赖字段的可选取值
    condValueOptions() {
      const dep = (this.fields || []).find(x => x.key === this.condKey)
      return dep ? (dep.options || []) : []
    },
    // 条件必填：是否可配置（总价/取值/只读文本/说明文字等自动字段除外）
    canConditionalRequired() {
      const t = this.field && this.field.type
      return !!t && t !== 'tips' && t !== 'total' && t !== 'sqlValue' && t !== 'readonlyText'
    },
    // 只读文本：可被引用其他字段 key（排除自身与其他只读文本，避免模板互相引用导致递归展开）
    readonlyRefKeys() {
      const keys = []
      ;(this.fields || []).forEach(x => {
        if (x.key && x.key !== (this.field && this.field.key) && x.type !== 'readonlyText') keys.push(x.key)
      })
      return keys
    },
    // 只读文本：content 中已引用的字段 key
    readonlyPlaceholders() {
      if (!this.field || this.field.type !== 'readonlyText') return []
      return readonlyTextPlaceholders(this.field.content)
    },
    // 只读文本：content 中引用无效（不存在，或指向只读文本自身同级字段）的 key
    unknownReadonlyKeys() {
      if (!this.field || !this.readonlyPlaceholders.length) return []
      const keys = {}
      this.readonlyRefKeys.forEach(k => { keys[k] = true })
      return this.readonlyPlaceholders.filter(k => !keys[k])
    },
    // 条件必填：开关
    condEnabled: {
      get() {
        return !!(this.field && this.field.requiredWhen && this.field.requiredWhen.key)
      },
      set(v) {
        if (!this.field) return
        if (!v) {
          this.$delete(this.field, 'requiredWhen')
          return
        }
        const first = this.condFieldOptions[0]
        if (!first) {
          this.$message.warning('请先添加带选项的字段（下拉/单选/多选）作为依赖字段')
          return
        }
        this.$set(this.field, 'requiredWhen', { key: first.key, values: [] })
      }
    },
    // 条件必填：依赖字段
    condKey: {
      get() {
        return (this.field && this.field.requiredWhen && this.field.requiredWhen.key) || ''
      },
      set(v) {
        if (this.field && this.field.requiredWhen) this.$set(this.field.requiredWhen, 'key', v)
      }
    },
    // 条件必填：命中取值
    condValues: {
      get() {
        const rw = this.field && this.field.requiredWhen
        return rw && Array.isArray(rw.values) ? rw.values : []
      },
      set(v) {
        if (this.field && this.field.requiredWhen) this.$set(this.field.requiredWhen, 'values', v)
      }
    },
    // 取值SQL中的 ${key} 占位列表
    sqlPlaceholders() {
      if (!this.field || this.field.type !== 'sqlValue' || !this.field.sql) return []
      const re = /\$\{([A-Za-z][A-Za-z0-9_]*)\}/g
      const out = []
      let m
      while ((m = re.exec(this.field.sql))) {
        if (out.indexOf(m[1]) < 0) out.push(m[1])
      }
      return out
    },
    sqlValueColumns() {
      return this.sqlValueRow ? Object.keys(this.sqlValueRow) : []
    },
    sqlValuePreview() {
      if (!this.sqlValueRow) return ''
      const cols = this.sqlValueColumns
      const col = this.field.valueCol || cols[0]
      const v = this.sqlValueRow[col]
      return v == null ? '—' : String(v)
    }
  },
  watch: {
    field() {
      this.parsedRows = null
      this.parsedColumns = []
      this.previewValue = []
      this.colParse = {}
      this.parsingColKey = ''
      this.sqlValueRow = null
      this.paramTest = {}
      this.colValueParse = {}
      this.colParams = {}
      // 旧 schema 商机/项目字段无 scope：补默认"全部"
      if (this.field && (this.field.type === 'chance' || this.field.type === 'project') && !this.field.scope) {
        this.$set(this.field, 'scope', 'all')
      }
      // 旧 schema 商机/项目字段无 scopeRules：补空数组
      if (this.field && (this.field.type === 'chance' || this.field.type === 'project') && !Array.isArray(this.field.scopeRules)) {
        this.$set(this.field, 'scopeRules', [])
      }
      // 用户角色：visible 未设置时默认显示
      if (this.field && this.field.type === 'userRole' && this.field.visible === undefined) {
        this.$set(this.field, 'visible', true)
      }
      // 金额：showUpper 未设置时默认开启（千分位 + 大写）
      if (this.field && this.field.type === 'money' && this.field.showUpper === undefined) {
        this.$set(this.field, 'showUpper', true)
      }
      // 只读文本：旧 schema 无 content 时补空串，保证 v-model 可用
      if (this.field && this.field.type === 'readonlyText' && this.field.content === undefined) {
        this.$set(this.field, 'content', '')
      }
      // 附件：不做默认补全（未配置视为「不限制」），保持旧表单行为不变
      ;((this.field && this.field.columns) || []).forEach(c => {
        if (c.type === 'money' && c.showUpper === undefined) {
          this.$set(c, 'showUpper', true)
        }
      })
      // 旧 schema 明细子列中商机/项目无 scope：补默认"全部"
      ;((this.field && this.field.columns) || []).forEach(c => {
        if ((c.type === 'chance' || c.type === 'project') && !c.scope) {
          this.$set(c, 'scope', 'all')
        }
      })
      // 条件必填：兼容旧 schema（补全缺失键；无依赖字段视为未配置）
      if (this.field && this.field.requiredWhen) {
        const rw = this.field.requiredWhen
        if (rw.key === undefined) this.$set(rw, 'key', '')
        if (!Array.isArray(rw.values)) this.$set(rw, 'values', [])
        if (!rw.key) this.$delete(this.field, 'requiredWhen')
      }
      this.sanitizeConstraints(this.field)
      ;((this.field && this.field.columns) || []).forEach(c => this.sanitizeConstraints(c))
    }
  },
  methods: {
    // 附件类型：预设分类多选（写回字段 schema）
    setAttachTypes(val) {
      if (!this.field) return
      this.$set(this.field, 'attachTypes', val || [])
    },
    // 附件类型：自定义扩展名补充（写回字段 schema）
    setAttachExts(val) {
      if (!this.field) return
      this.$set(this.field, 'attachExts', val || '')
    },
    // 条件必填：切换依赖字段时清空已选命中取值
    onCondKeyChange() {
      if (this.field && this.field.requiredWhen) this.$set(this.field.requiredWhen, 'values', [])
    },
    // 条件数据范围：新增一条规则
    addScopeRule() {
      if (!this.field) return
      if (!Array.isArray(this.field.scopeRules)) this.$set(this.field, 'scopeRules', [])
      const first = this.scopeRuleFieldOptions[0]
      this.field.scopeRules.push({ key: first ? first.key : '', values: [], scope: 'mine' })
    },
    // 条件数据范围：删除规则
    removeScopeRule(i) {
      if (this.field && Array.isArray(this.field.scopeRules)) this.field.scopeRules.splice(i, 1)
    },
    // 条件数据范围：切换驱动字段时清空已选命中取值
    onScopeRuleKeyChange(r) {
      this.$set(r, 'values', [])
    },
    // 条件数据范围：驱动字段的可选取值（用户角色→角色列表；带 options 字段→其选项；其余自由输入）
    scopeRuleValueOptions(r) {
      const dep = (this.fields || []).find(x => x.key === (r && r.key))
      if (!dep) return []
      if (dep.type === 'userRole') {
        return (this.roleOptions || []).map(x => ({ label: x.roleName || String(x.id), value: String(x.id) }))
      }
      if (Array.isArray(dep.options) && dep.options.length) {
        return dep.options.map(o => ({ label: o.label || o.value, value: String(o.value) }))
      }
      return []
    },
    addOption() {
      if (!this.field.options) this.$set(this.field, 'options', [])
      const n = this.field.options.length + 1
      this.field.options.push({ label: '选项' + n, value: String(n) })
    },
    removeOption(i) {
      this.field.options.splice(i, 1)
    },
    addColumn() {
      if (!this.field.columns) this.$set(this.field, 'columns', [])
      const n = this.field.columns.length + 1
      this.field.columns.push(createColumn('input', '列' + n))
    },
    removeColumn(i) {
      this.field.columns.splice(i, 1)
    },
    onColTypeChange(c) {
      if (c.type === 'money') {
        if (!c.unit) this.$set(c, 'unit', '元')
        if (c.showUpper === undefined) this.$set(c, 'showUpper', true)
      } else {
        if (c.unit !== undefined) this.$delete(c, 'unit')
        if (c.showUpper !== undefined) this.$delete(c, 'showUpper')
      }
      if (c.type === 'chance' || c.type === 'project') {
        if (c.scope === undefined) this.$set(c, 'scope', 'all')
        if (c.sql !== undefined) this.$delete(c, 'sql')
        if (c.levels !== undefined) this.$delete(c, 'levels')
        if (c.valueCol !== undefined) this.$delete(c, 'valueCol')
        if (c.constraints !== undefined) this.$delete(c, 'constraints')
      } else if (c.type === 'cascade') {
        if (c.sql === undefined) this.$set(c, 'sql', '')
        if (!Array.isArray(c.levels)) this.$set(c, 'levels', [])
        if (c.valueCol !== undefined) this.$delete(c, 'valueCol')
        if (c.scope !== undefined) this.$delete(c, 'scope')
      } else if (c.type === 'sqlValue') {
        if (c.sql === undefined) this.$set(c, 'sql', '')
        if (c.valueCol === undefined) this.$set(c, 'valueCol', '')
        if (c.levels !== undefined) this.$delete(c, 'levels')
        if (c.constraints !== undefined) this.$delete(c, 'constraints')
        if (c.scope !== undefined) this.$delete(c, 'scope')
      } else if (c.type === 'ctrlNumber') {
        if (!Array.isArray(c.constraints)) this.$set(c, 'constraints', [])
        this.sanitizeConstraints(c)
        if (c.sql !== undefined) this.$delete(c, 'sql')
        if (c.levels !== undefined) this.$delete(c, 'levels')
        if (c.valueCol !== undefined) this.$delete(c, 'valueCol')
        if (c.scope !== undefined) this.$delete(c, 'scope')
      } else {
        if (c.sql !== undefined) this.$delete(c, 'sql')
        if (c.levels !== undefined) this.$delete(c, 'levels')
        if (c.valueCol !== undefined) this.$delete(c, 'valueCol')
        if (c.constraints !== undefined) this.$delete(c, 'constraints')
        if (c.scope !== undefined) this.$delete(c, 'scope')
      }
    },
    // 受控数字：增删约束
    addConstraint(t) {
      if (!t.constraints) this.$set(t, 'constraints', [])
      t.constraints.push({ op: '<', mode: 'fixed', value: null, refKey: '' })
    },
    removeConstraint(t, i) {
      t.constraints.splice(i, 1)
    },
    // 受控数字：引用key 经 $set 写入，保证响应式
    setConstraintRefKey(ct, v) {
      this.$set(ct, 'refKey', v)
    },
    // 明细列宽：写回 columns[].width（空值删除该键，恢复自动均分）
    setColWidth(c, v) {
      const n = Number(v)
      if (v === null || v === undefined || v === '' || isNaN(n) || n <= 0) {
        if (c.width !== undefined) this.$delete(c, 'width')
        return
      }
      this.$set(c, 'width', Math.round(n))
    },
    // 受控数字：补全约束对象缺失键（兼容旧schema/类型切换残留）
    sanitizeConstraints(t) {
      if (!t || !Array.isArray(t.constraints)) return
      t.constraints.forEach(ct => {
        if (ct.op === undefined) this.$set(ct, 'op', '<')
        if (ct.mode === undefined) this.$set(ct, 'mode', 'fixed')
        if (ct.value === undefined) this.$set(ct, 'value', null)
        if (ct.refKey === undefined) this.$set(ct, 'refKey', '')
      })
    },
    // 明细级联子列：层级映射可选列
    colColumns(c) {
      const p = this.colParse[c.key]
      return p ? p.columns : []
    },
    // 明细级联子列：执行SQL并自动解析层级映射
    parseColSql(c) {
      if (!c.sql || !c.sql.trim()) {
        this.$message.warning('请先填写数据源SQL')
        return
      }
      this.parsingColKey = c.key
      queryDataset({ sql: c.sql })
        .then(res => {
          this.parsingColKey = ''
          if (!res || res.code !== 200) {
            this.$message.error((res && res.msg) || 'SQL解析失败')
            return
          }
          const rows = res.data || []
          const columns = rows.length ? Object.keys(rows[0]) : []
          this.$set(this.colParse, c.key, { rows, columns })
          const auto = parseLevelsFromColumns(columns)
          if (auto.length) {
            this.$set(c, 'levels', auto)
          } else if (!(c.levels || []).length) {
            this.$message.warning('未识别到 levelN_value / levelN_label 列，请手动配置层级映射')
          }
          this.$message.success('解析成功，共 ' + rows.length + ' 行')
        })
        .catch(() => { this.parsingColKey = '' })
    },
    addColLevel(c) {
      if (!c.levels) this.$set(c, 'levels', [])
      const n = c.levels.length + 1
      c.levels.push({ value: 'level' + n + '_value', label: 'level' + n + '_label' })
    },
    removeColLevel(c, i) {
      c.levels.splice(i, 1)
    },
    // 级联：执行自定义SQL并自动解析层级映射
    parseSql() {
      if (!this.field || !this.field.sql || !this.field.sql.trim()) {
        this.$message.warning('请先填写数据源SQL')
        return
      }
      this.parsing = true
      queryDataset({ sql: this.field.sql })
        .then(res => {
          this.parsing = false
          if (!res || res.code !== 200) {
            this.$message.error((res && res.msg) || 'SQL解析失败')
            return
          }
          const rows = res.data || []
          this.parsedRows = rows
          this.parsedColumns = rows.length ? Object.keys(rows[0]) : []
          const auto = parseLevelsFromColumns(this.parsedColumns)
          if (auto.length) {
            this.$set(this.field, 'levels', auto)
          } else if (!(this.field.levels || []).length) {
            this.$message.warning('未识别到 levelN_value / levelN_label 列，请手动配置层级映射')
          }
          this.previewValue = []
          this.$message.success('解析成功，共 ' + rows.length + ' 行')
        })
        .catch(() => { this.parsing = false })
    },
    addLevel() {
      if (!this.field.levels) this.$set(this.field, 'levels', [])
      const n = this.field.levels.length + 1
      this.field.levels.push({ value: 'level' + n + '_value', label: 'level' + n + '_label' })
    },
    // 总价：切换关联明细时重置列映射
    onTotalDetailChange() {
      this.$set(this.field, 'qtyCol', '')
      this.$set(this.field, 'priceCol', '')
    },
    // 取值子列：可引用 key 清单（兄弟子列key/级联层级别名 + 表单字段key/级联层级别名）
    refKeysHint(c) {
      const keys = []
      const push = x => {
        keys.push(x.key)
        if (x.type === 'cascade') {
          (x.levels || []).forEach(lv => {
            if (lv.value) keys.push(lv.value)
            if (lv.label && lv.label !== lv.value) keys.push(lv.label)
          })
        }
      }
      ;((this.field && this.field.columns) || []).forEach(x => { if (x.key !== (c && c.key)) push(x) })
      ;(this.fields || []).forEach(push)
      const uniq = []
      keys.forEach(k => { if (k && uniq.indexOf(k) < 0) uniq.push(k) })
      return uniq.join(', ') || '—'
    },
    // 字段级取值：可引用 key 清单（其他表单字段key + 级联层级别名）
    fieldRefKeysHint() {
      const keys = []
      ;(this.fields || []).forEach(x => {
        if (x.key !== (this.field && this.field.key)) keys.push(x.key)
        if (x.type === 'cascade') {
          (x.levels || []).forEach(lv => {
            if (lv.value) keys.push(lv.value)
            if (lv.label && lv.label !== lv.value) keys.push(lv.label)
          })
        }
      })
      const uniq = []
      keys.forEach(k => { if (k && uniq.indexOf(k) < 0) uniq.push(k) })
      return uniq.join(', ') || '—'
    },
    // 明细取值子列：SQL 中 ${key} 占位列表
    colSqlPlaceholders(c) {
      if (!c || !c.sql) return []
      const re = /\$\{([A-Za-z][A-Za-z0-9_]*)\}/g
      const out = []
      let m
      while ((m = re.exec(c.sql))) {
        if (out.indexOf(m[1]) < 0) out.push(m[1])
      }
      return out
    },
    // 明细取值子列：写入测试参数
    setColParam(c, k, v) {
      if (!this.colParams[c.key]) this.$set(this.colParams, c.key, {})
      this.$set(this.colParams[c.key], k, v)
    },
    // 明细取值子列：带测试参数执行SQL并缓存首行
    parseColSqlValue(c) {
      if (!c.sql || !c.sql.trim()) {
        this.$message.warning('请先填写取值SQL')
        return
      }
      this.parsingColKey = c.key
      querySqlValue({ sql: c.sql, params: this.colParams[c.key] || {} })
        .then(res => {
          this.parsingColKey = ''
          if (!res || res.code !== 200) {
            this.$message.error((res && res.msg) || 'SQL解析失败')
            return
          }
          const row = res.data || null
          if (!row) {
            this.$set(this.colValueParse, c.key, null)
            this.$message.warning('SQL执行成功但无数据行，请检查测试参数')
            return
          }
          this.$set(this.colValueParse, c.key, row)
          this.$message.success('解析成功，首行取值：' + this.colValuePreview(c))
        })
        .catch(() => { this.parsingColKey = '' })
    },
    // 明细取值子列：解析首行的列名
    colValueColumns(c) {
      const row = this.colValueParse[c.key]
      return row ? Object.keys(row) : []
    },
    // 明细取值子列：首行取值预览文本
    colValuePreview(c) {
      const row = this.colValueParse[c.key]
      if (!row) return ''
      const cols = Object.keys(row)
      const col = (c.valueCol && Object.prototype.hasOwnProperty.call(row, c.valueCol)) ? c.valueCol : cols[0]
      const v = row[col]
      return v == null ? '—' : String(v)
    },
    // 取值：带测试参数执行SQL并预览首行
    parseSqlValue() {
      if (!this.field || !this.field.sql || !this.field.sql.trim()) {
        this.$message.warning('请先填写取值SQL')
        return
      }
      this.parsing = true
      querySqlValue({ sql: this.field.sql, params: this.paramTest })
        .then(res => {
          this.parsing = false
          if (!res || res.code !== 200) {
            this.$message.error((res && res.msg) || 'SQL解析失败')
            return
          }
          this.sqlValueRow = res.data || null
          if (!this.sqlValueRow) {
            this.$message.warning('SQL执行成功但无数据行，请检查测试参数')
            return
          }
          this.$message.success('解析成功，首行取值：' + this.sqlValuePreview)
        })
        .catch(() => { this.parsing = false })
    },
    removeLevel(i) {
      this.field.levels.splice(i, 1)
    }
  }
}
</script>

<style scoped>
.wf-field-config {
  padding: 12px;
}
.wf-empty {
  color: #909399;
  text-align: center;
  padding: 40px 0;
  font-size: 13px;
}
.wf-hint {
  color: #C0C4CC;
  font-size: 12px;
  line-height: 1.4;
}
.wf-hint-warn {
  color: #E6A23C;
}
.wf-attach-types {
  display: flex;
  flex-wrap: wrap;
  row-gap: 4px;
}
.wf-attach-types .el-checkbox {
  margin-right: 12px;
}
.wf-opt-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
}
.wf-opt-del {
  color: #F56C6C;
  cursor: pointer;
}
.wf-col-card {
  border: 1px solid #EBEEF5;
  border-radius: 4px;
  padding: 8px;
  margin-bottom: 8px;
}
.wf-col-line {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
}
.wf-col-req {
  margin-left: auto;
}
.wf-cond-line {
  margin-top: 6px;
}
.wf-inline-hint {
  color: #909399;
  font-size: 12px;
}
.wf-lv-idx {
  width: 22px;
  color: #909399;
  font-size: 12px;
  flex-shrink: 0;
}
.wf-col-sql {
  margin: 6px 0;
}
.wf-ct-card {
  border: 1px dashed #EBEEF5;
  border-radius: 4px;
  padding: 6px;
  margin-bottom: 6px;
}
</style>
